import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Copy, ArrowUpRight, Link2 } from "lucide-react";
import { PageHeader } from "@/components/site/PageHeader";
import { Reveal } from "@/components/site/Reveal";

const LINKS = [
  { label: "Home", path: "/", desc: "Main website" },
  { label: "Services", path: "/services", desc: "Everything we offer", short: null },
  { label: "AT&T Hub", path: "/att", desc: "Wireless, fiber & pre-orders", short: "/wireless" },
  { label: "LegalShield", path: "/legalshield", desc: "Legal protection plans", short: "/legal" },
  { label: "KonnectMD", path: "/konnectmd", desc: "24/7 virtual healthcare", short: "/health" },
  { label: "Life Insurance", path: "/life-insurance", desc: "Protect your family's income", short: "/life" },
  { label: "Tax Services", path: "/taxes", desc: "Tax preparation & planning", short: "/tax" },
  { label: "Careers", path: "/careers", desc: "All four career paths", short: "/jobs" },
  { label: "Agent Application", path: "/careers/apply", desc: "Apply as a financial services agent", short: "/apply" },
  { label: "AT&T Rep Application", path: "/careers/att-apply", desc: "Apply as an AT&T sales rep", short: "/reps" },
  { label: "About", path: "/about", desc: "Our story & mission" },
  { label: "Meet the Owner", path: "/meet-the-owner", desc: "Get to know the founder", short: "/owner" },
  { label: "Meet the Team", path: "/meet-the-team", desc: "The people behind Heritage", short: "/team" },
  { label: "Partners", path: "/partners", desc: "Carriers & partnerships" },
  { label: "Blog", path: "/blog", desc: "News & insights" },
  { label: "Book a Call", path: "/book-a-call", desc: "Schedule time with us", short: "/book" },
  { label: "Contact", path: "/contact", desc: "Reach out any time" },
];

const copy = (url) => {
  navigator.clipboard.writeText(url).then(
    () => toast.success("Link copied — ready to share!"),
    () => toast.error("Couldn't copy. Long-press the link to copy manually."),
  );
};

export default function LinksPage() {
  const origin = window.location.origin;
  return (
    <>
      <PageHeader
        overline="Direct Links"
        title="Share any page"
        accent="in one tap."
        subtitle="Every page has its own direct link — and most have a short shortcut too. Tap copy and send it to anyone."
      />
      <section className="relative pb-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12 space-y-4" data-testid="links-list">
          {LINKS.map((l, i) => (
            <Reveal key={l.path} delay={(i % 4) * 0.03}>
              <div data-testid={`link-row-${l.path === "/" ? "home" : l.path.replace(/\//g, "-").replace(/^-/, "")}`} className="rounded-2xl border border-white/10 bg-[#0a192f] p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 hover:border-[#bf953f]/40 transition-colors duration-500">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[#bf953f]/30 text-[#d4af37]"><Link2 size={16} /></span>
                    <div>
                      <p className="font-serif-display text-xl text-white">{l.label}</p>
                      <p className="font-body text-xs text-white/45">{l.desc}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
                    <code className="font-body text-xs md:text-sm text-[#fcf6ba]/80 break-all">{origin}{l.path}</code>
                    {l.short && (
                      <span className="font-body text-[11px] text-white/40">shortcut: <code className="text-[#00A8E0]">{origin}{l.short}</code></span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => copy(`${origin}${l.short || l.path}`)}
                    data-testid={`copy-link-${l.path === "/" ? "home" : l.path.replace(/\//g, "-").replace(/^-/, "")}`}
                    className="btn-sweep inline-flex items-center gap-2 rounded-full bg-gold-metallic px-6 py-2.5 font-body text-xs font-semibold tracking-wide text-black"
                  >
                    <Copy size={14} /> Copy Link
                  </button>
                  <Link to={l.path} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/60 hover:border-[#d4af37] hover:text-[#d4af37] transition-colors" aria-label={`Open ${l.label}`}>
                    <ArrowUpRight size={16} />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
